import { useCallback, useMemo, useState } from 'react'
import { useSelector } from '@/libs/state'
import { groupWorkItemsByStep, type PhaseColumns } from '../application/group-work-items-by-step'
import type { CompanyWorkflow } from '../domain/workflow'
import { kanbanColumnOrder$, workItems$ } from '../store'

// Applies a saved column order to server-derived columns so drag-reordered columns
// survive work-item updates. Unknown step IDs are appended at the end.
const applyOrder = (raw: PhaseColumns, order: string[] | undefined): PhaseColumns => {
	if (!order) return raw
	const next: PhaseColumns = {}
	for (const id of order) {
		const items = raw[id]
		if (items) next[id] = items
	}
	for (const id of Object.keys(raw)) {
		if (!(id in next)) next[id] = raw[id]!
	}
	return next
}

// Server-derived columns are computed from workItems during render (no effect needed -
// the value is fully derivable from props/state). A local DnD layer sits on top so the
// Kanban component can show optimistic column reorders while a fireTransition is in-flight.
// When the server state changes (workItems$ updated after a successful transition), the
// render-time comparison detects the mismatch and resets the DnD layer to the new server
// state - no effect, no extra render cycle from an asynchronous setState.
const useKanbanColumns = (workflow: CompanyWorkflow) => {
	const workItems = useSelector(workItems$)
	const savedOrder = useSelector(kanbanColumnOrder$)

	const serverColumns = useMemo(() => {
		const next: Record<string, PhaseColumns> = {}
		for (const phase of workflow.phases) {
			next[phase.id] = applyOrder(groupWorkItemsByStep(phase, workItems), savedOrder[phase.id])
		}
		return next
	}, [workflow, workItems, savedOrder])

	// Render-time reset: when server columns change (reference changes because useMemo
	// recomputed), propagate the new derived value into the DnD layer immediately.
	// This is the React-documented "adjusting state on render" pattern - React throws
	// away the current render output and retries with the new state synchronously.
	const [dndColumns, setDndColumns] = useState(serverColumns)
	const [prevServerColumns, setPrevServerColumns] = useState(serverColumns)
	if (prevServerColumns !== serverColumns) {
		setPrevServerColumns(serverColumns)
		setDndColumns(serverColumns)
	}

	const setPhaseColumns = useCallback((phaseId: string, columns: PhaseColumns) => {
		setDndColumns(prev => ({ ...prev, [phaseId]: columns }))
	}, [])

	return { allColumns: dndColumns, setPhaseColumns }
}

export { useKanbanColumns }
